import {
  Pagination,
  type Selection,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@heroui/react';
import React, { useEffect, useState } from 'react';
import { FiInbox } from 'react-icons/fi';
import { LuLayoutGrid, LuLayoutList } from 'react-icons/lu';
import { Heading } from '@/core/molecules';
import { Button, Spinner } from '@/core/ui';
import {
  QuestionRecordReviewsDocument,
  type QuestionRecordReviewsQuery,
  type QuestionRecordReviewsQueryVariables,
  type RecordReview,
} from '@/graphql/gql/graphql';
import { NetworkStatus, useQuery } from '@/libs';
import { useContentReview } from '@/store';
import { FeedbackEditModal } from './feedback-edit-modal';
import { QuestionReviewsGrid } from './question-reviews-grid';
import { ReviewTableCell } from './review-table-cells';
import { TableHeaderActions } from './table-header-actions';
import { useReviewMutations } from './use-review-mutations';

export const LIMIT = 50;

export const columns: Array<{
  name: string;
  uid: string;
  sortable: boolean;
  width?: number;
}> = [
  { name: 'SR NO.', uid: 'srNo', sortable: false },
  { name: 'ID', uid: 'id', sortable: false },
  { name: 'C-ID', uid: 'commentId', sortable: false },
  { name: 'REASON', uid: 'reason', sortable: false },
  { name: 'R-ID', uid: 'recordId', sortable: false },
  { name: 'ASSIGNED', uid: 'member', sortable: false },
  { name: 'STATE', uid: 'state', sortable: false },
  { name: 'ACTIONED BY', uid: 'actionedBy', sortable: false },
  { name: 'CREATED AT', uid: 'createdAt', sortable: false },
  { name: 'BY', uid: 'user', sortable: false },
  { name: 'FEEDBACK', uid: 'comment', sortable: false },
  { name: 'ACTIONS', uid: 'actions', sortable: false },
];

type QuestionReviewsTableProps = {
  filterKey: string;
  search: string;
  createdAtUnix: number | undefined;
  createdBy: number | null;
  recordIds: number[];
  type: number | null;
  state: number | null;
  actionedBy: number | null;
  title: string;
  refetchKey?: number;
  columnKeysToHide?: string[];
};

export const QuestionReviewsTable: React.FC<QuestionReviewsTableProps> = ({
  actionedBy,
  recordIds,
  filterKey,
  search,
  createdAtUnix,
  createdBy,
  type,
  state,
  title,
  refetchKey,
  columnKeysToHide = [],
}) => {
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('grid');
  const selectedIds = useContentReview.use.selectedIds();
  const setSelectedIds = useContentReview.use.setSelectedIds();
  const {
    updateLoading,
    deleteLoading,
    handleStateChange,
    handleActionedByChange,
    handleMembersChange,
    handleCommentSave,
    handleDelete,
  } = useReviewMutations();

  const { data, loading, refetch, networkStatus } = useQuery<
    QuestionRecordReviewsQuery,
    QuestionRecordReviewsQueryVariables
  >(QuestionRecordReviewsDocument, {
    variables: {
      options: {
        page: page - 1 > 0 ? page : 1,
        take: LIMIT,
        comment: search || undefined,
        createdAt: createdAtUnix ? new Date(createdAtUnix * 1000).toISOString() : undefined,
        memberIds: createdBy ? [createdBy] : undefined,
        recordIds: recordIds.length > 0 ? recordIds : undefined,
        type: type ?? undefined,
        state: state ?? undefined,
        actionedBy: actionedBy ?? undefined,
      },
    },
  });

  const isLoading = loading || !data || networkStatus === NetworkStatus.refetch;
  const reviews = (data?.restricted?.recordReviews?.result ?? []) as RecordReview[];
  const rows = reviews.map((review, index) => ({
    review,
    serialNo: (page - 1) * LIMIT + index + 1,
  }));
  const total = data?.restricted?.recordReviews?.total ?? 0;

  useEffect(() => {
    setPage(1);
    refetch();
  }, [filterKey]);

  useEffect(() => {
    if (refetchKey !== undefined && refetchKey > 0) {
      refetch();
    }
  }, [refetchKey, refetch]);

  const handleSelectionChange = (keys: Selection) => {
    if (keys === 'all') {
      setSelectedIds(reviews.map((item) => item.id as string | number));
    } else {
      setSelectedIds(Array.from(keys) as (string | number)[]);
    }
  };

  const renderCell = React.useCallback(
    (review: RecordReview, columnKey: string, serialNo: number) => {
      return (
        <ReviewTableCell
          review={review}
          columnKey={columnKey}
          serialNo={serialNo}
          onStateChange={handleStateChange}
          onActionedByChange={handleActionedByChange}
          onMembersChange={handleMembersChange}
          onDelete={handleDelete}
          updateLoading={updateLoading}
          deleteLoading={deleteLoading}
        />
      );
    },
    [handleStateChange, handleMembersChange, handleDelete, updateLoading, deleteLoading],
  );

  const tableColumns = React.useMemo(() => {
    return columns.filter((c) => !columnKeysToHide.includes(c.uid));
  }, [columnKeysToHide]);

  return (
    <div className='bg-background rounded-lg shadow-md flex flex-col h-full'>
      <Heading
        description={
          <span className='text-sm font-medium text-warning-600'>
            Showing {reviews.length} reviews on page {page} of {Math.max(1, Math.ceil(total / LIMIT))} (Total:{' '}
            {total})
          </span>
        }
        textClassName='md:text-xl'
        title={title}
        className='md:px-6 py-2'
      >
        <div className='flex items-center gap-2'>
          <Button
            variant={viewMode === 'grid' ? 'solid' : 'bordered'}
            isIconOnly
            size='sm'
            onPress={() => setViewMode('grid')}
            aria-label='Grid view'
          >
            <LuLayoutGrid className='size-4' />
          </Button>
          <Button
            variant={viewMode === 'table' ? 'solid' : 'bordered'}
            isIconOnly
            size='sm'
            onPress={() => setViewMode('table')}
            aria-label='Table view'
          >
            <LuLayoutList className='size-4' />
          </Button>
          <TableHeaderActions onRefetch={() => refetch()} isLoading={isLoading} />
        </div>
      </Heading>

      {viewMode === 'grid' ? (
        isLoading ? (
          <div className='flex items-center justify-center min-h-[400px]'>
            <Spinner size='lg' />
          </div>
        ) : rows.length === 0 ? (
          <div className='flex flex-col space-y-4 items-center justify-center min-h-[400px]'>
            <FiInbox className='size-16 text-default-400' />
            <p className='text-default-400 text-2xl font-medium'>No record reviews found</p>
          </div>
        ) : (
          <QuestionReviewsGrid
            rows={rows}
            selectedIds={new Set(selectedIds as Iterable<string | number>)}
            onSelect={(id, selected) => {
              const next = new Set(selectedIds as Iterable<string | number>);
              if (selected) next.add(id);
              else next.delete(id);
              setSelectedIds(Array.from(next));
            }}
            onStateChange={handleStateChange}
            onActionedByChange={handleActionedByChange}
            onMembersChange={handleMembersChange}
            onDelete={handleDelete}
            deleteLoading={deleteLoading}
          />
        )
      ) : (
        <Table
          isHeaderSticky
          color='default'
          classNames={{
            base: 'max-h-[calc(100vh-400px)] overflow-auto',
            wrapper: 'px-6 scrollbar-thin',
            tr: 'hover:bg-default-100',
            th: [''],
          }}
          isCompact
          shadow='none'
          aria-label='Question record reviews table'
          selectionMode='multiple'
          selectedKeys={selectedIds}
          onSelectionChange={handleSelectionChange}
          onRowAction={(_key) => {}}
        >
          <TableHeader columns={tableColumns}>
            {(column) => (
              <TableColumn
                className='text-sm'
                key={column.uid}
                align={column.uid === 'actions' ? 'center' : 'start'}
              >
                {column.name}
              </TableColumn>
            )}
          </TableHeader>
          <TableBody
            emptyContent={
              <div className='col-span-full flex flex-col space-y-4 items-center justify-center h-full'>
                <FiInbox className='size-16 text-default-400' />
                <p className='text-default-400 text-2xl font-medium'>No record reviews found</p>
              </div>
            }
            isLoading={isLoading}
            loadingContent={<Spinner size='lg' />}
            items={rows}
          >
            {(item) => (
              <TableRow key={item.review.id || String(Math.random())}>
                {(columnKey) => (
                  <TableCell>{renderCell(item.review, columnKey as string, item.serialNo)}</TableCell>
                )}
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
      {total > 0 && (
        <div className='p-4 flex justify-between items-center border-t border-default-200'>
          <span className='w-[30%] text-small text-default-400'>
            Page {page} of {Math.max(1, Math.ceil(total / LIMIT))}
          </span>
          <Pagination
            isCompact
            showControls
            showShadow
            color='primary'
            page={page}
            total={Math.max(1, Math.ceil(total / LIMIT))}
            onChange={setPage}
          />
        </div>
      )}

      <FeedbackEditModal onSave={handleCommentSave} isLoading={updateLoading} />
    </div>
  );
};
